import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { PencilSimple, Pill, X } from "@phosphor-icons/react"
import i18next from "i18next"
import { Button } from "@/components/ui/button"
import { List, ListItem, ListText } from "@/components/ui/list"
import { DD_MM_YYYY, formatDateString } from "@/lib/format-date"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { ChMedicationDrawer } from "@/modules/patients/detail/medication/ch/ch-medication-drawer"
import { chMedicationFragmentInline$data } from "@/modules/patients/detail/medication/ch/services/__generated__/chMedicationFragmentInline.graphql"
import { DeMedicationDrawer } from "@/modules/patients/detail/medication/de/de-medication-drawer"
import { deMedicationFragmentInline$data } from "@/modules/patients/detail/medication/de/services/__generated__/deMedicationFragmentInline.graphql"
import { handleMedicationSearchItem } from "@/modules/patients/detail/medication/services/useChMedicationSearchItemQuery"
import { getRecommendedMedications } from "@/modules/patients/detail/progress-note/lib/is-recommended-medications-empty"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useDeleteRecommendedMedication } from "@/modules/patients/detail/recommended-medications/services/useDeleteRecommendedMedication"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { ChMedicationType, Language, MedicationUnit } from "@/types/__generated__/union-types"
interface MedicalDosage {
  eveningDosage: number
  morningDosage: number
  nightDosage: number
  noonDosage: number
  unit: MedicationUnit
}

interface ChMedicalCategory {
  name?: string | null
  templateCategory?: string | null
}

interface DeMedicalCategory {
  name?: string | null
  composition?: string | null
}

const formatUsage = (medication: MedicalDosage) => {
  if (medication.unit === "PRN") {
    return i18next.t("patient.medication.unitList.PRN")
  }
  const { eveningDosage, morningDosage, nightDosage, noonDosage } = medication
  return `${morningDosage}-${noonDosage}-${eveningDosage}-${nightDosage}`
}

type MedicationInfo = { type: "CH"; medication: ChMedicalCategory } | { type: "DE"; medication: DeMedicalCategory }
const getMedicationInfo = ({ medication, type }: MedicationInfo) => {
  if (type === "CH") {
    return joinStrings([medication.name, medication.templateCategory])
  } else {
    return joinStrings([medication.name, medication.composition])
  }
}

type RecommendedMedicationListProps = {
  progressNote: ProgressNoteData
  patientLanguage?: Language
  isEditable?: boolean
}
export const RecommendedMedicationList = (props: RecommendedMedicationListProps) => {
  const { isEditable = false, progressNote, patientLanguage } = props
  const { t } = useTranslation()
  const confirm = useConfirmStore((state) => state.confirm)
  const cancel = useConfirmStore((state) => state.cancel)
  const country = useCountryParam()
  const openDrawerWithoutConfirmation = useDrawerStore((state) => state.openDrawerWithoutConfirmation)
  const deleteRecommendedMedication = useDeleteRecommendedMedication(country)

  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const recommendedMedications = getRecommendedMedications(progressNote)

  if (!recommendedMedications || recommendedMedications.length === 0) {
    return null
  }

  const handleDeleteRecommendedMedication = (id: string, name: string) => {
    confirm({
      title: `${t("common.button.delete")} ${name}`,
      content: t("episodeOfCare.deleteProgressNote.content"),
      confirmVariant: "danger",
      confirmText: t("common.button.delete"),
      cancelText: t("common.button.keep"),
      onConfirm() {
        deleteRecommendedMedication({ progressNoteId: progressNote.id, id, onCompleted: cancel, onError: cancel })
      },
    })
  }

  const handleFetchChMedicationDetail = async (
    medicationType: ChMedicationType,
    pharmacode?: number | null,
    templateId?: number | null,
  ) => {
    if (medicationType === "PREPARATION" || medicationType === "TEMPLATE") {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      setIsLoading(true)

      const medicationDetail = await handleMedicationSearchItem(
        abortControllerRef.current.signal,
        medicationType,
        patientLanguage ?? "ENGLISH",
        templateId,
        pharmacode,
      )

      setIsLoading(false)
      return medicationDetail
    }
  }

  const handleEditRecommendedMedication = async (
    medication?: Omit<chMedicationFragmentInline$data | deMedicationFragmentInline$data, " $fragmentType">,
  ) => {
    if (!medication) return
    const recommendedMedication = { progressNoteId: progressNote.id, onAddSuccess: () => {} }

    switch (medication.__typename) {
      case "de_Medication": {
        openDrawerWithoutConfirmation(
          () => (
            <DeMedicationDrawer
              recommendedMedication={{
                editMedication: medication as deMedicationFragmentInline$data,
                ...recommendedMedication,
              }}
            />
          ),
          "MedicationDrawer",
        )
        break
      }
      case "ch_Medication": {
        const { name, pharmaCode, type, templateId } = medication as chMedicationFragmentInline$data

        const medicationDetail = await handleFetchChMedicationDetail(type, pharmaCode, templateId)

        const medicationDefault = {
          ...medicationDetail,
          itemId: type === "TEMPLATE" ? (templateId ?? undefined) : (pharmaCode ?? undefined),
          pharmacode: pharmaCode,
          name: name,
          medicationType: type,
        }

        openDrawerWithoutConfirmation(() => (
          <ChMedicationDrawer
            recommendedMedication={{
              editMedication: medication as chMedicationFragmentInline$data,
              medication: medicationDefault,
              ...recommendedMedication,
            }}
          />
        ))
        break
      }
      default:
        return
    }
  }

  return (
    <List className="mx-4 mb-4">
      {recommendedMedications.map(({ node }) => {
        const { medication, id } = node
        const medicationUsage = `${t("patient.medication.since")} ${joinStrings([formatDateString(medication.createdAt, DD_MM_YYYY), formatUsage(medication)])}`
        return (
          <ListItem
            key={medication.id}
            className={cn(
              "group hover:bg-surface-hovered flex h-12 items-center",
              isEditable && "hover:cursor-pointer",
            )}>
            <Pill className="text-on-surface-subdued shrink-0" />
            <div
              className="ml-2 min-w-0 group-hover:mr-4"
              data-testid={`recommended-medication-item-${medication.name.toLowerCase().replace(/\s/g, "-")}`}>
              <ListText>
                {getMedicationInfo(country === "CH" ? { type: "CH", medication } : { type: "DE", medication })}
              </ListText>
              <ListText variant="secondary" className={"truncate"}>
                {medicationUsage}
              </ListText>
            </div>
            {isEditable && (
              <div className="ml-auto hidden group-hover:block">
                <div className="flex">
                  <Button
                    data-testid="recommended-medication-item-delete"
                    className="h-10 w-10 transition-all"
                    variant="circle"
                    type="button"
                    onClick={() => handleDeleteRecommendedMedication(id, medication.name)}>
                    <X />
                  </Button>
                  <Button
                    data-testid="recommended-medication-item-edit"
                    className="h-10 w-10 transition-all"
                    variant="circle"
                    type="button"
                    disabled={isLoading}
                    onClick={() => handleEditRecommendedMedication(medication)}>
                    <PencilSimple />
                  </Button>
                </div>
              </div>
            )}
          </ListItem>
        )
      })}
    </List>
  )
}
